import * as yup from 'yup';

const requiredArrMinOne = yup.array().min(1).required();
const requiredString = yup.string().required();

const conditionArraysSchema = yup
  .object({
    condition1: yup.array().of(yup.string()).default([]),
    condition2: yup.array().of(yup.string()).default([]),
  })
  // eslint-disable-next-line func-names
  .test('condition1-condition2', 'Invalid selection', function (values) {
    const { condition1 = [], condition2 = [] } = values || {};
    const len1 = condition1.length;
    const len2 = condition2.length;

    // rule:
    // - if one is empty, other >= 2
    // - if one == 1, other >=1
    // - if one >=2, other not required
    if (len2 === 0 && len1 < 2) return this.createError({ path: 'condition1' });
    if (len1 === 0 && len2 < 2) return this.createError({ path: 'condition2' });
    if (len2 === 1 && len1 < 1) return this.createError({ path: 'condition1' });
    if (len1 === 1 && len2 < 1) return this.createError({ path: 'condition2' });

    return true;
  });

export const initialSchema = yup
  .object()
  .concat(conditionArraysSchema)
  .shape({
    // step 1
    confirmInitiatingVisit: requiredArrMinOne,
    visitDate: yup.date().when('confirmInitiatingVisit', {
      is: (arr?: string[]) => arr?.some((i) => i.includes('visitCompleted')),
      then: (schema) => schema.required(),
    }),
    visitType: yup.string().when('confirmInitiatingVisit', {
      is: (arr?: string[]) => arr?.some((i) => i.includes('visitCompleted')),
      then: (schema) => schema.required(),
    }),

    // step 2
    recentHealthcareActivity: requiredArrMinOne,
    providerAppointmentOtherText: yup
      .string()
      .when('recentHealthcareActivity', {
        is: (arr?: string[]) => arr?.some((i) => i.includes('other')),
        then: (schema) => schema.required(),
      }),
    dates: requiredString,
    details: requiredString,

    // step 3
    wasMedRecPerformed: requiredString,
    timing: requiredArrMinOne,
    inAdvanceOfAppointmentOnText: yup.string().when('timing', {
      is: (arr?: string[]) =>
        arr?.some((i) => i.includes('inAdvanceOfAppointmentOn')),
      then: (schema) => schema.required(),
    }),
    medicationDiscrepancies: requiredString,
    medicationDiscrepanciesYesText: yup
      .string()
      .when('medicationDiscrepancies', {
        is: (v?: string) => v === 'yes',
        then: (schema) => schema.required(),
      }),
    patientTakingMedicationsAsPrescribed: requiredString,
    patientTakingMedicationsAsPrescribedNoText: yup
      .string()
      .when('patientTakingMedicationsAsPrescribed', {
        is: (v?: string) => v === 'no',
        then: (schema) => schema.required(),
      }),

    // step 4
    condition1DiagnosisText: yup.string().when('condition1', {
      is: (arr?: string[]) =>
        arr?.some((i) => i.includes('condition1Diagnosis')),
      then: (schema) => schema.required(),
    }),
    condition1DiagnosisICDText: yup.string().when('condition1', {
      is: (arr?: string[]) =>
        arr?.some((i) => i.includes('condition1Diagnosis')),
      then: (schema) => schema.required(),
    }),
    condition1CurrentInterventionsText: yup.string().when('condition1', {
      is: (arr?: string[]) =>
        arr?.some((i) => i.includes('condition1CurrentInterventions')),
      then: (schema) => schema.required(),
    }),
    condition1MeasurableGoalText: yup.string().when('condition1', {
      is: (arr?: string[]) =>
        arr?.some((i) => i.includes('condition1MeasurableGoal')),
      then: (schema) => schema.required(),
    }),
    condition2DiagnosisText: yup.string().when('condition2', {
      is: (arr?: string[]) =>
        arr?.some((i) => i.includes('condition2Diagnosis')),
      then: (schema) => schema.required(),
    }),
    condition2DiagnosisICDText: yup.string().when('condition2', {
      is: (arr?: string[]) =>
        arr?.some((i) => i.includes('condition2Diagnosis')),
      then: (schema) => schema.required(),
    }),
    condition2CurrentInterventionsText: yup.string().when('condition2', {
      is: (arr?: string[]) =>
        arr?.some((i) => i.includes('condition2CurrentInterventions')),
      then: (schema) => schema.required(),
    }),
    condition2MeasurableGoalText: yup.string().when('condition2', {
      is: (arr?: string[]) =>
        arr?.some((i) => i.includes('condition2MeasurableGoal')),
      then: (schema) => schema.required(),
    }),
    individualizedCarePlanCreated: requiredString,
    carePlanCreatedYesList: yup.array().when('individualizedCarePlanCreated', {
      is: (v?: string) => v === 'yes',
      then: (schema) => schema.min(1).required(),
    }),
    interventionsList: yup.array().when('carePlanCreatedYesList', {
      is: (arr?: string[]) => arr?.some((i) => i.includes('interventions')),
      then: (schema) => schema.min(1).required(),
    }),
    medicationManagementList: yup.array().when('interventionsList', {
      is: (arr?: string[]) =>
        arr?.some((i) => i.includes('medicationManagement')),
      then: (schema) => schema.min(1).required(),
    }),
    educationAndSelfMonitoringList: yup.array().when('interventionsList', {
      is: (arr?: string[]) =>
        arr?.some((i) => i.includes('educationAndSelfMonitoring')),
      then: (schema) => schema.min(1).required(),
    }),
    careCoordinationList: yup.array().when('interventionsList', {
      is: (arr?: string[]) => arr?.some((i) => i.includes('careCoordination')),
      then: (schema) => schema.min(1).required(),
    }),
    goalList: yup.array().when('carePlanCreatedYesList', {
      is: (arr?: string[]) => arr?.some((i) => i.includes('goals')),
      then: (schema) => schema.min(1).required(),
    }),
    barriers: requiredArrMinOne,
    barriersOtherText: yup.string().when('barriers', {
      is: (arr?: string[]) => arr?.some((i) => i.includes('other')),
      then: (schema) => schema.required(),
    }),
    additionalBarrierNotes: requiredString,
    planIncludes: requiredArrMinOne,

    // step 5
    phq2Completed: requiredString,
    phq2CompletedYesText: yup.string().when('phq2Completed', {
      is: (v?: string) => v === 'yes',
      then: (schema) => schema.required(),
    }),
    phq2CompletedNoText: yup.string().when('phq2Completed', {
      is: (v?: string) => v === 'no',
      then: (schema) => schema.required(),
    }),
    sdohAssessmentCompleted: requiredString,
    anyUnmetNeeds: requiredString,
    anyUnmetNeedsYesText: yup.string().when('anyUnmetNeeds', {
      is: (v?: string) => v === 'yes',
      then: (schema) => schema.required(),
    }),
  });

export const monthlySchema = yup.object().shape({
  // STEP1
  hasRecentHealthcareActivity: requiredString,
  recentHealthcareActivity: yup.array().when('hasRecentHealthcareActivity', {
    is: (v?: string) => v === 'yes',
    then: (schema) => schema.min(1).required(),
  }),
  recentHealthcareActivityOtherText: yup
    .string()
    .when('recentHealthcareActivity', {
      is: (arr?: string[]) => arr?.some((i) => i.includes('other')),
      then: (schema) => schema.required(),
    }),

  // STEP2
  wasMedRecPerformed: requiredString,
  timing: yup.array().when('wasMedRecPerformed', {
    is: (v?: string) => v === 'yes',
    then: (schema) => schema.min(1).required(),
  }),
  medicationDiscrepancies: requiredString,
  medicationDiscrepanciesYesText: yup.string().when('medicationDiscrepancies', {
    is: (v?: string) => v === 'yes',
    then: (schema) => schema.required(),
  }),
  patientAdherent: requiredString,
  patientAdherentNoText: yup.string().when('patientAdherent', {
    is: (v?: string) => v === 'no',
    then: (schema) => schema.required(),
  }),
  adherenceBarriersDiscussed: requiredString,
  adherenceBarriersIndicated: yup.string().when('adherenceBarriersDiscussed', {
    is: (v?: string) => v === 'yes',
    then: (schema) => schema.required(),
  }),
  adherenceBarriersIndicatedYesText: yup
    .string()
    .when('adherenceBarriersIndicated', {
      is: (v?: string) => v === 'yes',
      then: (schema) => schema.required(),
    }),
  changesSideEffectsNoted: requiredString,
  changesSideEffectsNotedYesText: yup.string().when('changesSideEffectsNoted', {
    is: (v?: string) => v === 'yes',
    then: (schema) => schema.required(),
  }),

  // STEP3
  didNurseUpdateCarePlan: requiredArrMinOne,
  goals: requiredArrMinOne,
  betterControlOfList: yup.array().when('goals', {
    is: (arr?: string[]) => arr?.some((i) => i.includes('betterControlOf')),
    then: (schema) => schema.min(1).required(),
  }),
  goalsOtherText: yup.string().when('goals', {
    is: (arr?: string[]) => arr?.some((i) => i.includes('other')),
    then: (schema) => schema.required(),
  }),
  measurableTargets: requiredString,
  measurableTargetsOtherText: yup.string().when('measurableTargets', {
    is: (v?: string) => v === 'other',
    then: (schema) => schema.required(),
  }),
  barriers: requiredArrMinOne,
  barriersOtherText: yup.string().when('barriers', {
    is: (arr?: string[]) => arr?.some((i) => i.includes('other')),
    then: (schema) => schema.required(),
  }),
  dateCarePlanLastUpdated: yup.date().required(),
  carePlanReviewedAtThisEncounter: requiredString,

  // STEP4
  adlFunctionalStatusChecked: yup.string(),
  cognitiveStatusScreened: yup.string(),
  behavioralHealthScreening: yup.string(),
  fallSafetyRiskAssessed: yup.string(),
  escalationsRedFlagsToday: yup.string(),
  escalationsRedFlagsTodayYesText: yup.string(),

  // STEP5
  sdohNeedsAssessmentUpdated: requiredString,
  anyUnmetNeedsOrReferralsThisMonth: requiredString,
  anyUnmetNeedsOrReferralsThisMonthYesText: yup
    .string()
    .when('anyUnmetNeedsOrReferralsThisMonth', {
      is: (v?: string) => v === 'yes',
      then: (schema) => schema.required(),
    }),

  // STEP6
  outstandingItemsNextDue: requiredString,
  preventiveCareReviewed: requiredString,

  // STEP7
  coordinationWithProviders: requiredString,
  anyReferralsFollowUps: requiredString,
  anyReferralsFollowUpsYesText: yup.string().when('anyReferralsFollowUps', {
    is: (v?: string) => v === 'yes',
    then: (schema) => schema.required(),
  }),
});
